import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { LogIn, Moon, Sun, Heart, Tag, PackageCheck, Menu, X, BookOpen, Plus, ShoppingCart, History } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UserMenu() {
  const [categories, setCategories] = useState([]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [showFavorites, setShowFavorites] = useState(false);
  const [shopOpen, setShopOpen] = useState(true);
  const [buttonLocks, setButtonLocks] = useState({});
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const { carts, addItem, refreshCarts } = useCart();

  useEffect(() => {
    fetchCategories();
    fetchMenuItems();
    fetchShopStatus();
    loadFavorites();
    if (user) refreshCarts({ silent: true });
  }, []);

  useEffect(() => {
    if (user) {
      refreshCarts({ silent: true });
    }
  }, [user, refreshCarts]);


  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchMenuItems = async () => {
    try {
      const response = await axios.get(`${API}/menu-items`);
      setMenuItems(response.data);
    } catch (error) {
      console.error('Failed to fetch menu items:', error);
    }
  };


  const fetchShopStatus = async () => {
    try {
      const response = await axios.get(`${API}/shop-status`);
      setShopOpen(response.data?.shop_open !== false);
    } catch (error) {
      setShopOpen(true);
    }
  };

  const loadFavorites = () => {
    const stored = localStorage.getItem('favorites');
    if (stored) {
      setFavorites(JSON.parse(stored));
    }
  };


  const addToCart = useCallback(async (item) => {
    if (!user) {
      navigate('/user-login');
      return;
    }
    if (!shopOpen) {
      toast.error('Shop is closed');
      return;
    }

    const hasActiveCounterCart = carts.some((cart) => cart.cart_status === 'pending_counter');
    if (hasActiveCounterCart) {
      toast.error('You already have an active cart. Please pay at the counter before adding new items.');
      return;
    }

    setButtonLocks((prev) => ({ ...prev, [item.id]: true }));
    try {
      addItem(item, 1).catch(() => {});
      toast.success('Added to cart', { duration: 1000 });
    } catch (error) {
      toast.error('Could not add item to cart');
    } finally {
      setTimeout(() => {
        setButtonLocks((prev) => ({ ...prev, [item.id]: false }));
      }, 120);
    }
  }, [user, shopOpen, carts, addItem, navigate]);

  const cartItemCount = useMemo(() => {
    const draft = carts.find((c) => c.cart_status === 'active' || c.cart_status === 'pending_payment');
    return (draft?.items || []).reduce((sum, line) => sum + line.quantity, 0);
  }, [carts]);

  const toggleFavorite = (itemId) => {
    let newFavorites;
    if (favorites.includes(itemId)) {
      newFavorites = favorites.filter(id => id !== itemId);
    } else {
      newFavorites = [...favorites, itemId];
      // Log favorite to backend for analytics
      axios.post(`${API}/analytics/favorite?item_id=${itemId}`).catch(err => console.error('Failed to log favorite:', err));
    }
    setFavorites(newFavorites);
    localStorage.setItem('favorites', JSON.stringify(newFavorites));
  };


  const filteredItems = selectedCategory
    ? menuItems.filter(item => item.category_id === selectedCategory)
    : menuItems;

  const displayedItems = showFavorites
    ? menuItems.filter(item => favorites.includes(item.id))
    : filteredItems;

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-[#020617]' : 'bg-slate-50'}`}>
      <div className={`fixed top-0 left-0 right-0 z-40 backdrop-blur-md border-b ${theme === 'dark'
          ? 'bg-black/40 border-white/10'
          : 'bg-white/60 border-slate-200/50'
        }`}>
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
      <img 
        src="logo.png" 
        alt="K-Crunch Logo" 
        className="h-8 w-auto md:h-10 object-contain" 
      />
      <h1 className={`font-serif text-2xl md:text-3xl ${theme === 'dark' ? 'text-amber-50' : 'text-slate-900'}`} data-testid="restaurant-name">
        K-Crunch
      </h1>
    </div>

          {/* DESKTOP MENU (Hidden on mobile) */}
          <div className="hidden md:flex gap-2">
            {user && (
              <Button
                onClick={() => navigate('/cart')}
                variant="ghost"
                className={theme === 'dark' ? 'text-amber-50 hover:text-amber-400' : 'text-slate-900 hover:text-amber-600'}
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                Cart ({cartItemCount})
              </Button>
            )}
            {user && (
              <Button
                onClick={() => navigate('/orders')}
                variant="ghost"
                className={theme === 'dark' ? 'text-amber-50 hover:text-amber-400' : 'text-slate-900 hover:text-amber-600'}
              >
                <History className="w-4 h-4 mr-2" />
                My Orders
              </Button>
            )}
            <Button
              onClick={() => navigate('/track')}
              variant="ghost"
              className={theme === 'dark' ? 'text-amber-50 hover:text-amber-400' : 'text-slate-900 hover:text-amber-600'}
            >
              <PackageCheck className="w-4 h-4 mr-2" />
              Track Order
            </Button>
            <Button
              onClick={() => navigate('/offers')}
              variant="ghost"
              className={theme === 'dark' ? 'text-amber-50 hover:text-amber-400' : 'text-slate-900 hover:text-amber-600'}
            >
              <Tag className="w-4 h-4 mr-2" />
              Offers
            </Button>
            <Button
              onClick={() => navigate('/surveys')}
              variant="ghost"
              className={theme === 'dark' ? 'text-amber-50 hover:text-amber-400' : 'text-slate-900 hover:text-amber-600'}
            >
              <BookOpen className="w-4 h-4 mr-2" />
              Surveys
            </Button>
            <Button
              onClick={toggleTheme}
              variant="ghost"
              size="icon"
              className={theme === 'dark' ? 'text-amber-50 hover:text-amber-400' : 'text-slate-900 hover:text-amber-600'}
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            {user ? (
              <Button
                onClick={logout}
                className="bg-red-500 hover:bg-red-600 text-white font-bold ml-2"
              >
                Logout
              </Button>
            ) : (
              <Button
                onClick={() => navigate('/user-login')}
                className="bg-amber-400 hover:bg-amber-500 text-black font-bold ml-2 shadow-sm"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Login
              </Button>
            )}
          </div>

          {/* MOBILE MENU TRIGGER (Visible only on mobile) */}
          <div className="md:hidden flex items-center gap-2">
            <Button
              onClick={toggleTheme}
              variant="ghost"
              size="icon"
              className={theme === 'dark' ? 'text-amber-50' : 'text-slate-900'}
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <Button
              onClick={() => setIsMobileMenuOpen(true)}
              variant="ghost"
              size="icon"
              className={theme === 'dark' ? 'text-amber-50' : 'text-slate-900'}
            >
              <Menu className="w-6 h-6" />
            </Button>
            
          </div>
        </div>
      </div>


      {/* Hero Section */}
      <div className="relative h-[60vh] overflow-hidden">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1657593088889-5105c637f2a8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxkYXJrJTIwbW9vZHklMjByZXN0YXVyYW50JTIwaW50ZXJpb3IlMjBkaW5pbmd8ZW58MHx8fHwxNzY5MjM0NzMwfDA&ixlib=rb-4.1.0&q=85)'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/50 to-transparent" />
        </motion.div>

        <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-serif text-5xl md:text-7xl font-bold tracking-tight leading-none text-amber-50">
              Exquisite Flavors
            </h2>
            <p className="font-serif text-xl md:text-3xl text-amber-100/90 mt-4">
              Crafted with passion, served with love
            </p>
          </motion.div>
        </div>
      </div>

      {/* Category Navigation */}
      <div className={`sticky top-0 z-20 py-4 ${theme === 'dark' ? 'glassmorphism' : 'bg-white/90 backdrop-blur-sm border-b border-slate-200'}`}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-2 overflow-x-auto pb-2">
            <Button
              onClick={() => { setSelectedCategory(null); setShowFavorites(false); }}
              variant={selectedCategory === null && !showFavorites ? 'default' : 'ghost'}
              className={selectedCategory === null && !showFavorites ? 'bg-amber-500 text-black hover:bg-amber-400' : theme === 'dark' ? 'text-slate-300 hover:text-amber-400' : 'text-slate-700 hover:text-amber-600'}
              data-testid="category-all-button"
            >
              All Items
            </Button>
            <Button
              onClick={() => setShowFavorites(!showFavorites)}
              variant={showFavorites ? 'default' : 'ghost'}
              className={showFavorites ? 'bg-red-500 text-white hover:bg-red-400' : theme === 'dark' ? 'text-slate-300 hover:text-red-400' : 'text-slate-700 hover:text-red-600'}
              data-testid="favorites-button"
            >
              <Heart className={`w-4 h-4 mr-2 ${showFavorites ? 'fill-current' : ''}`} />
              Favorites {favorites.length > 0 && `(${favorites.length})`}
            </Button>
            {categories.map((category) => (
              <Button
                key={category.id}
                onClick={() => { setSelectedCategory(category.id); setShowFavorites(false); }}
                variant={selectedCategory === category.id ? 'default' : 'ghost'}
                className={selectedCategory === category.id ? 'bg-amber-500 text-black hover:bg-amber-400' : theme === 'dark' ? 'text-slate-300 hover:text-amber-400' : 'text-slate-700 hover:text-amber-600'}
                data-testid={`category-${category.name.toLowerCase()}-button`}
              >
                {category.name}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Menu Items Grid */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {displayedItems.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card className={`group relative overflow-hidden rounded-2xl border ${theme === 'dark' ? 'bg-black border-slate-800' : 'bg-white border-slate-200'} transition-all duration-300 h-full flex flex-col ${!item.available ? 'opacity-70' : ''}`} data-testid={`menu-item-${item.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={item.image_url || item.image_data || '/logo.png'}
                    alt={item.title}
                    onError={(e) => { e.currentTarget.src = '/logo.png'; }}
                    className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${!item.available ? 'grayscale' : ''}`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/45 to-transparent" />
                  {!item.available && (
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                      <Badge className="bg-red-600 text-white text-lg px-4 py-2">Out of Stock</Badge>
                    </div>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFavorite(item.id);
                    }}
                    className={`absolute top-3 right-3 p-2 rounded-full ${favorites.includes(item.id) ? 'bg-red-500' : 'bg-black/50 hover:bg-black/70'} transition-colors`}
                    data-testid={`favorite-button-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <Heart className={`w-5 h-5 text-white ${favorites.includes(item.id) ? 'fill-current' : ''}`} />
                  </button>

                  <div className="absolute left-4 right-4 bottom-4 text-white">
                    <h3 className="font-serif text-2xl font-semibold" data-testid={`item-title-${item.title.toLowerCase().replace(/\s+/g, '-')}`}>
                      {item.title}
                    </h3>
                    <p className="text-sm text-white/85 mt-1 line-clamp-2" data-testid={`item-description-${item.title.toLowerCase().replace(/\s+/g, '-')}`}>
                      {item.description}
                    </p>
                  </div>
                </div>

                <CardContent className={`p-4 border-t ${theme === 'dark' ? 'border-slate-800 bg-black' : 'border-slate-200 bg-white'}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        <p className={`text-3xl font-bold leading-none ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`} data-testid={`item-price-${item.title.toLowerCase().replace(/\s+/g, '-')}`}>
                          ₹{item.price}
                        </p>
                        {item.actual_price && item.actual_price > item.price && (
                          <p className={`text-xl line-through ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>
                            ₹{item.actual_price}
                          </p>
                        )}
                        {item.actual_price && item.actual_price > item.price && (
                          <Badge className="bg-green-600 text-white font-semibold px-3 py-1">
                            Save ₹{Math.round(item.actual_price - item.price)}
                          </Badge>
                        )}
                      </div>
                    </div>

                    <Button
                      onClick={() => addToCart(item)}
                      disabled={!shopOpen || !item.available || buttonLocks[item.id]}
                      title={!shopOpen ? 'Shop is closed' : ''}
                      className="bg-rose-600 hover:bg-rose-500 text-white min-w-[132px] text-lg font-semibold rounded-xl disabled:opacity-50"
                    >
                      <Plus className="w-5 h-5 mr-1" />
                      {buttonLocks[item.id] ? 'Adding...' : 'Add'}
                    </Button>
                  </div>
                  {!shopOpen && <p className="text-xs text-amber-600 mt-2">Shop is closed</p>}
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>

        {displayedItems.length === 0 && (
          <div className="text-center py-16" data-testid="no-items-message">
            <p className={`text-lg ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
              {showFavorites ? 'No favorites yet. Add items to favorites!' : 'No items available in this category.'}
            </p>
          </div>
        )}
      </div>

      {/* MOBILE SIDEBAR (Drawer) */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop (Dark overlay) */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMobileMenuOpen(false)}
              className="fixed inset-0 bg-black/60 z-40 backdrop-blur-sm md:hidden"
            />

            {/* Sidebar Panel */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className={`fixed top-0 right-0 h-full w-3/4 max-w-[300px] z-50 shadow-2xl p-6 flex flex-col gap-6 md:hidden ${theme === 'dark' ? 'bg-slate-900 border-l border-slate-800' : 'bg-white'
                }`}
            >
              {/* Header inside Sidebar */}
              <div className="flex justify-between items-center">
                <span className={`font-serif text-xl ${theme === 'dark' ? 'text-amber-50' : 'text-slate-900'}`}>
                  Menu
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={theme === 'dark' ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-black'}
                >
                  <X className="w-6 h-6" />
                </Button>
              </div>

              {/* Sidebar Links */}
              <div className="flex flex-col gap-3 mt-4">
                {user && (
                  <Button
                    onClick={() => { navigate('/cart'); setIsMobileMenuOpen(false); }}
                    variant="ghost"
                    className={`justify-start text-lg h-12 ${theme === 'dark' ? 'text-amber-50 hover:bg-slate-800' : 'text-slate-900 hover:bg-slate-100'}`}
                  >
                    <ShoppingCart className="w-5 h-5 mr-3" />
                    Cart
                  </Button>
                )}
                {user && (
                  <Button
                    onClick={() => { navigate('/orders'); setIsMobileMenuOpen(false); }}
                    variant="ghost"
                    className={`justify-start text-lg h-12 ${theme === 'dark' ? 'text-amber-50 hover:bg-slate-800' : 'text-slate-900 hover:bg-slate-100'}`}
                  >
                    <History className="w-5 h-5 mr-3" />
                    My Orders
                  </Button>
                )}
                <Button
                  onClick={() => { navigate('/track'); setIsMobileMenuOpen(false); }}
                  variant="ghost"
                  className={`justify-start text-lg h-12 ${theme === 'dark' ? 'text-amber-50 hover:bg-slate-800' : 'text-slate-900 hover:bg-slate-100'}`}
                >
                  <PackageCheck className="w-5 h-5 mr-3" />
                  Track Order
                </Button>

                <Button
                  onClick={() => { navigate('/offers'); setIsMobileMenuOpen(false); }}
                  variant="ghost"
                  className={`justify-start text-lg h-12 ${theme === 'dark' ? 'text-amber-50 hover:bg-slate-800' : 'text-slate-900 hover:bg-slate-100'}`}
                >
                  <Tag className="w-5 h-5 mr-3" />
                  Offers
                </Button>

                <Button
                  onClick={() => { navigate('/surveys'); setIsMobileMenuOpen(false); }}
                  variant="ghost"
                  className={`justify-start text-lg h-12 ${theme === 'dark' ? 'text-amber-50 hover:bg-slate-800' : 'text-slate-900 hover:bg-slate-100'}`}
                >
                  <div className="w-5 h-5 mr-3 flex items-center justify-center"><BookOpen className="w-4 h-4 mr-2" /></div>
                  Surveys
                </Button>
                {user ? (
              <Button
                onClick={logout}
                className="bg-red-500 hover:bg-red-600 text-white font-bold ml-2"
              >
                Logout
              </Button>
            ) : (
              <Button
                onClick={() => navigate('/user-login')}
                className="bg-amber-400 hover:bg-amber-500 text-black font-bold ml-2 shadow-sm"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Login
              </Button>
            )}
              </div>

              {/* Footer/Decoration at bottom of sidebar */}
              <div className="mt-auto pt-6 border-t border-slate-200 dark:border-slate-800">
                <p className="text-xs text-center text-slate-500">
                  © Waffle & Whisk
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

    </div>
  );
}
